import { useEffect } from "react";
import { connectionPoolManager } from "@/shared/services/supabase/connection-pool-manager";
import { store } from "@/core/redux/store";
import { setCurrentTokenBalance } from "@/core/redux/slices/user";
import { loggerService } from "@/shared/services/logger-service";

type SubscriptionEntry = {
  refCount: number;
  channelId: string;
  subscriptionId: string;
};

const subscriptionRegistry = new Map<string, SubscriptionEntry>();

function createSubscription(userId: string): SubscriptionEntry {
  const key = `current-token-balance:${userId}`;
  const subscriptionId = `${key}:${Math.random().toString(36).slice(2)}`;

  const channelId = connectionPoolManager.subscribeToBroadcast(subscriptionId, {
    channelName: `user-prt-balance:${userId}`,
    broadcastEvent: "prt-token-deducted",
    callback: (payload) => {
      const { newBalance } = payload.payload;
      if (typeof newBalance === "number" && !isNaN(newBalance)) {
        store.dispatch(setCurrentTokenBalance(newBalance));
      } else {
        loggerService.log(`Invalid balance received: ${newBalance}`);
      }
    },
  });

  return {
    refCount: 0,
    channelId,
    subscriptionId,
  };
}

export default function useUserTokenBalanceSubscription(
  userId?: string | null
) {
  useEffect(() => {
    if (!userId) return;

    const key = `current-token-balance:${userId}`;
    let entry = subscriptionRegistry.get(key);

    if (!entry) {
      entry = createSubscription(userId);
      subscriptionRegistry.set(key, entry);
    }

    entry.refCount += 1;

    return () => {
      const currentEntry = subscriptionRegistry.get(key);
      if (!currentEntry) return;

      currentEntry.refCount -= 1;

      if (currentEntry.refCount <= 0) {
        connectionPoolManager.unsubscribe(
          currentEntry.subscriptionId,
          currentEntry.channelId
        );
        subscriptionRegistry.delete(key);
      }
    };
  }, [userId]);
}
